import { useCallback, useEffect, useRef, useState } from "react";
import "./Deposit.css";
import DepositCreditCardForm from "./DepositCreditCardForm";
import AmountForm from "./AmountForm";
import WalletButton from "./WalletButton";
import { PAYMENT_OPTION_INTENT } from "./constants";
import VoucherForm from "./VoucherForm";
import ContactForm from "./ContactForm";
import PaymentResultModal from "./PaymentResultModal";
import { usePaymentStopwatch } from "./usePaymentStopwatch";
import {
  formatDepositHistoryMessage,
  upsertDepositHistoryEntry
} from "./depositHistory";

/* global PGDataType */

const PARTIAL_CONFIRMATION_TIMEOUT_SECONDS = 10;

const createDepositAttemptId = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `deposit-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getDepositIdFromValue = (value) =>
  value?.depositId ||
  value?.id ||
  value?.deposit?.depositId ||
  value?.deposit?.id ||
  value?.result?.depositId ||
  value?.result?.deposit?.depositId ||
  value?.result?.deposit?.id;

const getDepositId = (...values) =>
  values.map(getDepositIdFromValue).find(Boolean);

const getPaymentOptionClassName = (paymentOption) => {
  const toSlug = (value) =>
    `${value || "unknown"}`
      .replace(/([a-z])([A-Z])/g, "$1-$2")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");

  return [
    "payment-option-row",
    `payment-option-type-${toSlug(paymentOption.dataType)}`,
    `payment-option-name-${toSlug(paymentOption.displayName)}`
  ].join(" ");
};

function Deposit() {
  const sdk = window.__PGWebSdk;
  const {
    start: startPaymentStopwatch,
    pause: pausePaymentAttempt,
    resume: resumePaymentAttempt,
    finish: finishPaymentAttempt,
    reset: resetPaymentAttempt,
    close: closePaymentResult,
    paymentResult
  } = usePaymentStopwatch();

  const [paymentOptions, setPaymentOptions] = useState([]);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState(null);
  const [amount, setAmount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentOptionsLoaded, setPaymentOptionsLoaded] = useState(false);
  const [paymentOptionsLoadError, setPaymentOptionsLoadError] = useState(null);
  const [ctaUrl, setCtaUrl] = useState(null);
  const [isCtaModalOpen, setIsCtaModalOpen] = useState(false);
  const [cashTransferCode, setCashTransferCode] = useState(null);
  const [userCards, setUserCards] = useState([]);
  const [currentDepositId, setCurrentDepositId] = useState(null);
  const [bonusCode, setBonusCode] = useState("");
  const [feeBreakdownHash, setFeeBreakdownHash] = useState("");
  const [feeBreakdown, setFeeBreakdown] = useState([]);
  const [partialConfirmation, setPartialConfirmation] = useState(null);
  const [partialConfirmationCountdown, setPartialConfirmationCountdown] =
    useState(PARTIAL_CONFIRMATION_TIMEOUT_SECONDS);
  const [partialConfirmationProcessing, setPartialConfirmationProcessing] =
    useState(false);
  const [cvvRequiredAction, setCvvRequiredAction] = useState(null);
  const [cvvRequiredActionValue, setCvvRequiredActionValue] = useState("");

  useEffect(() => {
    document.body.classList.toggle(
      "loading-overlay-open",
      loading || Boolean(cvvRequiredAction)
    );

    return () => {
      document.body.classList.remove("loading-overlay-open");
    };
  }, [cvvRequiredAction, loading]);

  const buildDepositHistoryEntry = (attemptId, data, overrides = {}) => ({
    attemptId,
    status: "Pending",
    amount: data?.amount,
    currency: sdk.currency,
    bonusCode: data?.bonusCode,
    paymentOption: selectedPaymentOption
      ? {
          id: selectedPaymentOption.id,
          displayName: selectedPaymentOption.displayName,
          dataType: selectedPaymentOption.dataType
        }
      : null,
    data,
    request: data,
    ...overrides
  });

  const fetchDepositHistorySnapshot = useCallback(
    async (depositId, historyEntry) => {
      if (!depositId || !historyEntry) {
        return null;
      }

      try {
        const deposit = await sdk.getDepositAsync(depositId);
        upsertDepositHistoryEntry({
          ...historyEntry,
          depositId,
          deposit
        });
        return deposit;
      } catch (error) {
        upsertDepositHistoryEntry({
          ...historyEntry,
          depositId,
          depositFetchError:
            formatDepositHistoryMessage(error.message) ||
            formatDepositHistoryMessage(error)
        });
        return null;
      }
    },
    [sdk]
  );

  const fetchPaymentOptions = async (ct) => {
    try {
      const data = await sdk.getPaymentOptionsAsync(
        PAYMENT_OPTION_INTENT.Deposit,
        ct
      );

      const options = Array.isArray(data) ? data : [];
      setPaymentOptions(options);
      setPaymentOptionsLoaded(true);

      if (options.length === 0) {
        const error = new Error("No deposit payment options were returned.");
        setSelectedPaymentOption(null);
        setPaymentOptionsLoadError(error);
        setError(error);
        return;
      }

      setPaymentOptionsLoadError(null);
      setError(null);
      setSelectedPaymentOption(options[0]);
    } catch (error) {
      console.error(error);
      setPaymentOptionsLoaded(true);
      setPaymentOptionsLoadError(error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserCards = async (ct) => {
    try {
      const data = await sdk.getUserCardsAsync(ct);
      setUserCards(data);
      setError(null);
    } catch (error) {
      console.error(error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeeBreakdown = useCallback(
    async (ct) => {
      try {
        if (!amount || amount <= 0) {
          setFeeBreakdownHash("");
          setFeeBreakdown([]);
          setError(null);
          return;
        }

        const response = await sdk.getDepositFeeBreakdownAsync({ amount }, ct);

        setFeeBreakdownHash(response.feeBreakdownHash);
        setFeeBreakdown(response.feeBreakdown);

        setError(null);
      } catch (error) {
        console.error(error);
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [amount]
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const sessionId = queryParams.get("sessionId");
    if (typeof sessionId !== "string" || sessionId.length === 0) {
      setPaymentOptionsLoaded(true);
      setError(new Error("Session ID is not provided"));
      return;
    }

    const abortController = new AbortController();
    setLoading(true);
    fetchPaymentOptions(abortController.signal);
    fetchUserCards(abortController.signal);
    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (paymentOptionsLoadError) {
      return undefined;
    }

    const abortController = new AbortController();

    const timeoutId = setTimeout(() => {
      setLoading(true);
      fetchFeeBreakdown(abortController.signal);
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [fetchFeeBreakdown, paymentOptionsLoadError]);

  useEffect(() => {
    if (!ctaUrl || !isCtaModalOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setIsCtaModalOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [ctaUrl, isCtaModalOpen]);

  const handlePaymentStatus = useCallback(
    (result, historyEntry) => {
      const { status, errorCode, error } = result;
      const depositId = getDepositId(result, historyEntry);
      const errorMessage =
        formatDepositHistoryMessage(result.errorMessage) ||
        formatDepositHistoryMessage(error?.message) ||
        formatDepositHistoryMessage(error);

      if (historyEntry) {
        const nextHistoryEntry = {
          ...historyEntry,
          status,
          depositId: depositId || historyEntry.depositId,
          errorCode,
          errorMessage,
          error,
          result: depositId ? { ...result, depositId } : result
        };

        upsertDepositHistoryEntry(nextHistoryEntry);
        fetchDepositHistorySnapshot(
          nextHistoryEntry.depositId,
          nextHistoryEntry
        );
      }

      const failureDetails = [errorCode, errorMessage]
        .filter(Boolean)
        .join(": ");
      const paymentResultMessages = {
        Succeeded: "Your payment succeeded.",
        Failed: failureDetails
          ? `Your payment failed: ${failureDetails}`
          : "Your payment failed.",
        Initiated: "Your payment will be done later automatically.",
        Canceled: "Your payment was canceled."
      };

      finishPaymentAttempt({
        title: "Payment result",
        status: status || "Unknown",
        message:
          paymentResultMessages[status] ||
          `Payment status is not supported: ${status}`,
        details: [
          errorCode ? ["Error code", errorCode] : null,
          errorMessage ? ["Error message", errorMessage] : null,
          depositId ? ["Deposit ID", depositId] : null
        ].filter(Boolean)
      });

      setCurrentDepositId(null);
      setCvvRequiredAction(null);
      setCvvRequiredActionValue("");
      setError(null);
    },
    [fetchDepositHistorySnapshot, finishPaymentAttempt]
  );

  const openPartialConfirmation = useCallback(
    (result, historyEntry) => {
      const { requiredAction } = result;
      const depositId = getDepositId(result, historyEntry);
      const parameters = requiredAction?.parameters || {};
      const nextHistoryEntry = historyEntry
        ? {
            ...historyEntry,
            status: "Partial Pending",
            depositId,
            requiredAction: requiredAction?.action,
            result: depositId ? { ...result, depositId } : result
          }
        : null;

      if (nextHistoryEntry) {
        upsertDepositHistoryEntry(nextHistoryEntry);
        fetchDepositHistorySnapshot(depositId, nextHistoryEntry);
      }

      setPartialConfirmation({
        depositId,
        requested: parameters.requested,
        available: parameters.available,
        currency: parameters.currency,
        historyEntry: nextHistoryEntry
      });
      setPartialConfirmationCountdown(PARTIAL_CONFIRMATION_TIMEOUT_SECONDS);
      setPartialConfirmationProcessing(false);
      setCurrentDepositId(depositId);
      setError(null);
    },
    [fetchDepositHistorySnapshot]
  );

  const completePartialDeposit = useCallback(
    async (action) => {
      if (!partialConfirmation || partialConfirmationProcessing) {
        return;
      }

      resumePaymentAttempt();
      setPartialConfirmationProcessing(true);
      setLoading(true);

      try {
        const result =
          action === "cancel"
            ? await sdk.cancelPartialDepositAsync(partialConfirmation.depositId)
            : await sdk.continuePartialDepositAsync(
                partialConfirmation.depositId
              );

        setPartialConfirmation(null);
        setPartialConfirmationCountdown(PARTIAL_CONFIRMATION_TIMEOUT_SECONDS);
        setCurrentDepositId(null);

        if (action === "cancel") {
          if (partialConfirmation.historyEntry) {
            const canceledHistoryEntry = {
              ...partialConfirmation.historyEntry,
              status: "Partial Canceled",
              depositId: partialConfirmation.depositId,
              result: { ...result, depositId: partialConfirmation.depositId }
            };

            upsertDepositHistoryEntry(canceledHistoryEntry);
            fetchDepositHistorySnapshot(
              partialConfirmation.depositId,
              canceledHistoryEntry
            );
          }

          finishPaymentAttempt({
            title: "Payment result",
            status: "Partial Canceled",
            message: "Partial deposit canceled.",
            details: [["Deposit ID", partialConfirmation.depositId]]
          });
          setError(null);
          return;
        }

        handlePaymentStatus(
          {
            ...result,
            depositId: getDepositId(result, partialConfirmation)
          },
          partialConfirmation.historyEntry
        );
      } catch (error) {
        console.error(error);

        if (partialConfirmation.historyEntry) {
          upsertDepositHistoryEntry({
            ...partialConfirmation.historyEntry,
            status: "Crashed",
            depositId: partialConfirmation.depositId,
            errorMessage:
              formatDepositHistoryMessage(error.message) ||
              formatDepositHistoryMessage(error),
            error
          });
        }

        setPartialConfirmationCountdown(PARTIAL_CONFIRMATION_TIMEOUT_SECONDS);
        setError(error);
      } finally {
        setPartialConfirmationProcessing(false);
        setLoading(false);
      }
    },
    [
      handlePaymentStatus,
      fetchDepositHistorySnapshot,
      finishPaymentAttempt,
      resumePaymentAttempt,
      partialConfirmation,
      partialConfirmationProcessing,
      sdk
    ]
  );

  useEffect(() => {
    if (!partialConfirmation || partialConfirmationProcessing) {
      return;
    }

    if (partialConfirmationCountdown <= 0) {
      completePartialDeposit("continue");
      return;
    }

    const timeoutId = setTimeout(() => {
      setPartialConfirmationCountdown((seconds) => Math.max(seconds - 1, 0));
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    completePartialDeposit,
    partialConfirmation,
    partialConfirmationCountdown,
    partialConfirmationProcessing
  ]);

  const buildPaymentData = (data) => {
    const paymentData = { ...data, amount, feeBreakdownHash };

    if (
      !selectedPaymentOption?.disableBonusCode &&
      typeof bonusCode === "string" &&
      bonusCode.length > 0
    ) {
      paymentData.bonusCode = bonusCode;
    }

    return paymentData;
  };

  const beginPaymentAttempt = (data) => {
    startPaymentStopwatch();
    setLoading(true);

    const historyEntry = buildDepositHistoryEntry(
      createDepositAttemptId(),
      data
    );
    upsertDepositHistoryEntry(historyEntry);

    return historyEntry;
  };

  // A wallet button click reads its payment data here, so the attempt starts at the click.
  const walletAttemptRef = useRef(null);
  const getWalletData = () => {
    // A click while a wallet payment runs is dropped by the SDK, so it keeps the running attempt.
    if (walletAttemptRef.current) {
      return walletAttemptRef.current.data;
    }

    const data = buildPaymentData();
    const historyEntry = beginPaymentAttempt(data);
    walletAttemptRef.current = {
      data,
      historyEntry: {
        ...historyEntry,
        request: { paymentOption: selectedPaymentOption, data }
      }
    };

    return data;
  };

  // A wallet click's payAsync result gets the same handling as any other payment.
  const handleWalletResult = (result) => {
    const attempt = walletAttemptRef.current ?? {};
    walletAttemptRef.current = null;

    handlePaymentResult(result, { ...attempt, deposit: null });
    setLoading(false);
  };

  // Handles a payAsync result: a required action, or the final status.
  const handlePaymentResult = (
    result,
    { data, deposit, historyEntry, request }
  ) => {
    const { requiredAction, errorCode } = result;
    const depositId = getDepositId(result, deposit, historyEntry);
    const resultWithDepositId = depositId ? { ...result, depositId } : result;

    if (requiredAction && requiredAction.action) {
      const actionHistoryEntry = {
        ...historyEntry,
        status: "Action Required",
        depositId,
        requiredAction: requiredAction.action,
        ctaUrl: requiredAction.ctaUrl,
        cashTransferCode: requiredAction.cashTransferCode,
        errorCode,
        errorMessage: formatDepositHistoryMessage(result.errorMessage),
        result: resultWithDepositId
      };

      pausePaymentAttempt();

      switch (requiredAction.action) {
        case "ConfirmPartialDeposit": {
          openPartialConfirmation(result, actionHistoryEntry);
          return result;
        }
        case "InsertCardData": {
          finishPaymentAttempt({
            title: "Payment action required",
            status: "Action Required",
            message: "Insert your card details to continue this payment.",
            details: [
              ["Required action", requiredAction.action],
              depositId ? ["Deposit ID", depositId] : null
            ].filter(Boolean)
          });
          break;
        }
        case "InsertCvv": {
          upsertDepositHistoryEntry(actionHistoryEntry);
          fetchDepositHistorySnapshot(depositId, actionHistoryEntry);
          setCurrentDepositId(depositId);
          setCvvRequiredAction({
            data,
            depositId,
            historyEntry: actionHistoryEntry
          });
          setCvvRequiredActionValue("");
          setError(null);
          return result;
        }
        case "Redirect": {
          upsertDepositHistoryEntry(actionHistoryEntry);
          fetchDepositHistorySnapshot(depositId, actionHistoryEntry);
          setCtaUrl(requiredAction.ctaUrl);
          setIsCtaModalOpen(true);
          setCurrentDepositId(null);
          finishPaymentAttempt({
            title: "Payment action required",
            status: "Action Required",
            message: "Follow the provider link to continue this payment.",
            details: [
              ["Required action", requiredAction.action],
              depositId ? ["Deposit ID", depositId] : null
            ].filter(Boolean)
          });
          return request;
        }
        case "PayByCode": {
          upsertDepositHistoryEntry(actionHistoryEntry);
          fetchDepositHistorySnapshot(depositId, actionHistoryEntry);
          setCashTransferCode(requiredAction.cashTransferCode);
          setCtaUrl(null);
          setIsCtaModalOpen(false);
          setCurrentDepositId(null);
          finishPaymentAttempt({
            title: "Payment action required",
            status: "Action Required",
            message: "Use the given payment code to continue this payment.",
            details: [
              ["Required action", requiredAction.action],
              requiredAction.cashTransferCode
                ? ["Payment code", requiredAction.cashTransferCode]
                : null,
              depositId ? ["Deposit ID", depositId] : null
            ].filter(Boolean)
          });
          return request;
        }
        default: {
          finishPaymentAttempt({
            title: "Unsupported required action",
            status: "Action Required",
            message:
              "Required action is not supported: " + requiredAction.action,
            details: [
              ["Required action", requiredAction.action],
              depositId ? ["Deposit ID", depositId] : null
            ].filter(Boolean)
          });
          break;
        }
      }

      upsertDepositHistoryEntry(actionHistoryEntry);
      fetchDepositHistorySnapshot(depositId, actionHistoryEntry);
      setCurrentDepositId(depositId);

      return result;
    }

    handlePaymentStatus(resultWithDepositId, historyEntry);

    return result;
  };

  const tryExecutePayment = async (data) => {
    let historyEntry = null;

    try {
      data = buildPaymentData(data);
      historyEntry = beginPaymentAttempt(data);

      const deposit = currentDepositId
        ? await sdk.getDepositAsync(currentDepositId)
        : null;
      const existingDepositId = getDepositId(deposit, {
        depositId: currentDepositId
      });
      if (existingDepositId) {
        historyEntry = {
          ...historyEntry,
          depositId: existingDepositId,
          deposit
        };
        upsertDepositHistoryEntry(historyEntry);
      }

      const request = {
        paymentOption: selectedPaymentOption,
        deposit: deposit,
        data
      };
      historyEntry = { ...historyEntry, request };
      upsertDepositHistoryEntry(historyEntry);

      const result = await sdk.payAsync(request);

      return handlePaymentResult(result, {
        data,
        deposit,
        historyEntry,
        request
      });
    } catch (error) {
      console.error(error);

      if (historyEntry) {
        const errorMessage =
          formatDepositHistoryMessage(error.message) ||
          formatDepositHistoryMessage(error);

        upsertDepositHistoryEntry({
          ...historyEntry,
          status: "Crashed",
          depositId: getDepositId(historyEntry, {
            depositId: currentDepositId
          }),
          errorMessage,
          error
        });

        finishPaymentAttempt({
          title: "Payment error",
          status: "Crashed",
          message: errorMessage
            ? `Payment crashed: ${errorMessage}`
            : "Payment crashed.",
          details: [["Error message", errorMessage]].filter(([, value]) =>
            Boolean(value)
          )
        });
      }

      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const closeCvvRequiredAction = () => {
    resetPaymentAttempt();
    setCvvRequiredAction(null);
    setCvvRequiredActionValue("");
  };

  const completeCvvRequiredAction = async (event) => {
    event.preventDefault();

    if (!cvvRequiredAction || !cvvRequiredActionValue.trim()) {
      return;
    }

    const nextData = {
      ...cvvRequiredAction.data,
      ccCvv: cvvRequiredActionValue.trim()
    };

    setCvvRequiredAction(null);
    setCvvRequiredActionValue("");
    await tryExecutePayment(nextData);
  };

  if (!paymentOptionsLoaded || paymentOptionsLoadError) {
    return (
      <div className="App">
        {loading && !paymentOptionsLoadError && (
          <div
            className="loading"
            role="status"
            aria-live="polite"
            aria-label="Loading"
          >
            <span className="loading-spinner" aria-hidden="true"></span>
            <span className="loading-text">Loading</span>
          </div>
        )}
        {paymentOptionsLoadError && (
          <div className="error">
            An error occurred:{" "}
            {formatDepositHistoryMessage(paymentOptionsLoadError.message) ||
              formatDepositHistoryMessage(paymentOptionsLoadError)}
            <p>{paymentOptionsLoadError.stack}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="App">
      {loading && (
        <div
          className="loading"
          role="status"
          aria-live="polite"
          aria-label="Loading"
        >
          <span className="loading-spinner" aria-hidden="true"></span>
          <span className="loading-text">Loading</span>
        </div>
      )}

      {partialConfirmation && (
        <div className="partial-confirmation-overlay">
          <div className="partial-confirmation-dialog">
            <h2>Partial deposit available</h2>
            <p>
              The available funds are lower than the requested deposit amount.
            </p>

            <div className="partial-confirmation-amounts">
              <div>
                <span>Requested</span>
                <strong>
                  {partialConfirmation.requested} {partialConfirmation.currency}
                </strong>
              </div>
              <div>
                <span>Available</span>
                <strong>
                  {partialConfirmation.available} {partialConfirmation.currency}
                </strong>
              </div>
            </div>

            <p>
              Continuing automatically in {partialConfirmationCountdown}{" "}
              seconds.
            </p>

            <div className="partial-confirmation-actions">
              <button
                type="button"
                onClick={() => completePartialDeposit("continue")}
                disabled={partialConfirmationProcessing}
              >
                Continue
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => completePartialDeposit("cancel")}
                disabled={partialConfirmationProcessing}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {ctaUrl && isCtaModalOpen && (
        <div
          className="cta-modal-overlay"
          onClick={() => setIsCtaModalOpen(false)}
        >
          <div
            className="cta-modal-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cta-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="cta-modal-header">
              <div>
                <h2 id="cta-modal-title">Continue deposit</h2>
                <p>Open the provider CTA URL to complete this payment.</p>
              </div>
              <button
                type="button"
                className="history-close-button cta-modal-close"
                onClick={() => setIsCtaModalOpen(false)}
                aria-label="Close CTA modal"
              >
                <span className="close-icon" aria-hidden="true"></span>
              </button>
            </div>

            <a
              className="cta-modal-url"
              href={ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {ctaUrl}
            </a>

            <div className="cta-modal-actions">
              <button
                type="button"
                className="secondary-action"
                onClick={() => setIsCtaModalOpen(false)}
              >
                Close
              </button>
              <a
                className="primary-action cta-modal-primary"
                href={ctaUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open CTA
              </a>
            </div>
          </div>
        </div>
      )}

      {cvvRequiredAction && (
        <div className="cvv-modal-overlay" onClick={closeCvvRequiredAction}>
          <form
            className="cvv-modal-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cvv-modal-title"
            onSubmit={completeCvvRequiredAction}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="cta-modal-header">
              <div>
                <h2 id="cvv-modal-title">Insert CVV</h2>
                <p>Enter the card CVV to continue this deposit.</p>
              </div>
              <button
                type="button"
                className="history-close-button cta-modal-close"
                onClick={closeCvvRequiredAction}
                aria-label="Close CVV modal"
              >
                <span className="close-icon" aria-hidden="true"></span>
              </button>
            </div>

            <div className="form-group cvv-modal-field">
              <label htmlFor="required-action-cvv">CVV</label>
              <input
                id="required-action-cvv"
                name="required-action-cvv"
                type="password"
                inputMode="numeric"
                autoComplete="cc-csc"
                value={cvvRequiredActionValue}
                onChange={(event) =>
                  setCvvRequiredActionValue(event.target.value)
                }
                placeholder="Enter CVV"
                autoFocus
                required
              />
            </div>

            <div className="cta-modal-actions">
              <button
                type="button"
                className="secondary-action"
                onClick={closeCvvRequiredAction}
              >
                Close
              </button>
              <button type="submit" className="primary-action">
                Continue
              </button>
            </div>
          </form>
        </div>
      )}

      <PaymentResultModal result={paymentResult} onClose={closePaymentResult} />

      {error && (
        <div className="error">
          An error occurred:{" "}
          {formatDepositHistoryMessage(error.message) ||
            formatDepositHistoryMessage(error)}
          <p>{error.stack}</p>
        </div>
      )}

      {cashTransferCode && (
        <p className="payment-code">Payment code: {cashTransferCode}</p>
      )}

      <h1>Payment Options</h1>

      <div className="payment-options">
        {paymentOptions.map((paymentOption) => (
          <div
            className={getPaymentOptionClassName(paymentOption)}
            key={paymentOption.id}
          >
            <input
              id={paymentOption.id}
              type="radio"
              name="paymentOption"
              value={paymentOption.id}
              checked={paymentOption.id === selectedPaymentOption?.id}
              onChange={() => setSelectedPaymentOption(paymentOption)}
            />
            <label htmlFor={paymentOption.id}>
              {paymentOption.displayName}
            </label>
          </div>
        ))}
      </div>

      <div className="separator"></div>

      <div className="selected-payment-option">
        Selected Payment Option: {selectedPaymentOption?.displayName || "None"}
        {selectedPaymentOption?.minAmount && (
          <div className="min-amount">
            Min Amount: {selectedPaymentOption.minAmount} {sdk.currency}
          </div>
        )}
        {selectedPaymentOption?.maxAmount && (
          <div className="max-amount">
            Max Amount: {selectedPaymentOption.maxAmount} {sdk.currency}
          </div>
        )}
        {selectedPaymentOption?.disableBonusCode && (
          <div className="disable-bonus-code">Bonus codes: not accepted</div>
        )}
      </div>

      <div className="separator"></div>

      <div className="grid form-group">
        {selectedPaymentOption &&
          /* eslint-disable-next-line no-undef */
          selectedPaymentOption.dataType !== PGDataType.Voucher && (
            <div className="amount">
              <label htmlFor="amount">Amount:</label>
              <input
                type="number"
                id="amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />

              <div className="fee-breakdown">
                {feeBreakdown.map((fee, index) => (
                  <div key={index} className="fee-breakdown-item">
                    {fee.ruleName}: {fee.calculatedFee}
                  </div>
                ))}
              </div>
            </div>
          )}

        {!selectedPaymentOption?.disableBonusCode && (
          <div className="bonusCode">
            <label htmlFor="bonusCode">Bonus Code:</label>
            <input
              type="text"
              id="bonusCode"
              value={bonusCode}
              onChange={(e) => setBonusCode(e.target.value)}
            />
          </div>
        )}
      </div>

      {selectedPaymentOption && (
        <div className="form">
          {/* eslint-disable-next-line no-undef */}
          {selectedPaymentOption.dataType === PGDataType.CreditCard && (
            <DepositCreditCardForm
              onSubmit={tryExecutePayment}
              userCards={userCards}
            />
          )}

          {/* eslint-disable-next-line no-undef */}
          {selectedPaymentOption.dataType === PGDataType.Amount && (
            <AmountForm onSubmit={tryExecutePayment} />
          )}

          {/* eslint-disable-next-line no-undef */}
          {selectedPaymentOption.dataType === PGDataType.Voucher && (
            <VoucherForm onSubmit={tryExecutePayment} />
          )}

          {/* eslint-disable-next-line no-undef */}
          {selectedPaymentOption.dataType === PGDataType.Email && (
            <ContactForm
              field="email"
              label="Email"
              inputType="email"
              autoComplete="email"
              placeholder="Enter email"
              onSubmit={tryExecutePayment}
            />
          )}

          {/* eslint-disable-next-line no-undef */}
          {selectedPaymentOption.dataType === PGDataType.PhoneNumber && (
            <ContactForm
              field="phoneNumber"
              label="Phone Number"
              inputType="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="0755111222"
              hint="With or without the country code, e.g. 0755111222 or +40755111222."
              onSubmit={tryExecutePayment}
            />
          )}

          {/* eslint-disable-next-line no-undef */}
          {[PGDataType.GooglePay, PGDataType.ApplePay].includes(
            selectedPaymentOption.dataType
          ) && (
            <WalletButton
              sdk={sdk}
              paymentOption={selectedPaymentOption}
              getData={getWalletData}
              onResult={handleWalletResult}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default Deposit;
